import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	FormLabel,
	Grid,
	Theme,
	Typography,
	makeStyles
} from "@material-ui/core";

import {
	TextField,
	identityRecordOfFieldEditorOptions
} from "@ploy-ui/form-fields";
import { useCallback } from "react";
import { isArray } from "lodash";
import { InputFieldProps } from "../..";
import { IntlShape, defineMessages, useIntl } from "react-intl";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { v4 as uuid } from "uuid";
import { Formik, Field as FormikField } from "formik";
import { SectionProps } from "..";
import { addRegisteredSectionLayout } from "../sectionLayoutDescriptions";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import {
	ApplicationCustomerDetailsDTO,
	CustomerRole,
	CustomerType,
	ErrorDisplay
} from "@ploy-lib/types";

import { CalcRuleFormikField } from "../../field/CalcRuleFormikField";
import {
	ValidationHelpers,
	createValidationHelpers
} from "@ploy-lib/validation-helpers";
import { Endpoint } from "@rest-hooks/endpoint";

export interface ExtendedApplicationCustomer
	extends ApplicationCustomerDetailsDTO {
	_frontendOnlyId?: string;
	_isNewEntry?: boolean;
}

const useStyles = makeStyles(
	(theme: Theme) => ({
		root: {},
		listItem: {
			padding: theme.spacing(2)
		},
		expanded: {
			backgroundColor: theme.palette.primary.light,
			border: `1px solid ${theme.palette.secondary.light}`
		},
		childNumber: {
			paddingBottom: theme.spacing(2),
			paddingTop: theme.spacing(2)
		},
		header: {
			fontWeight: 700,
			paddingBottom: theme.spacing(4),
			padding: theme.spacing(0.5)
		},
		lenderLabel: {
			overflowWrap: "anywhere",
			fontWeight: "bold"
		},
		boldText: {
			fontWeight: "bold",
			fontSize: 16
		},
		addButton: {
			borderRadius: 4,
			justifyContent: "start",
			padding: "12px 24px",
			border: `1px solid ${theme.palette.common.black}`,
			fontSize: 16,
			background: "#fff",
			marginTop: theme.spacing(2)
		},
		addButtonPlusIcon: {
			marginRight: 8
		},
		expandMoreIcon: {
			color: theme.palette.primary.main
		},
		spacingAboveField: {
			paddingTop: 12
		}
	}),
	{ name: "DployHouseOwnersFromApplicationCustomerField" }
);

const messages = defineMessages({
	houseOwnerTitle: {
		id: "template-form.field.houseOwner.houseOwnerTitle",
		defaultMessage: "House owner"
	},
	name: {
		id: "template-form.field.houseOwner.name",
		defaultMessage: "Name"
	},
	ssn: {
		id: "template-form.field.houseOwner.ssn",
		defaultMessage: "Social security number"
	},
	mustBeValidSsn: {
		id: "template-form.field.houseOwner.mustBeValidSsn",
		defaultMessage: "SSN must be valid"
	},
	email: {
		id: "template-form.field.houseOwner.email",
		defaultMessage: "E-mail address"
	},
	phone: {
		id: "template-form.field.houseOwner.phone",
		defaultMessage: "Phone number"
	},
	save: {
		id: "template-form.field.houseOwner.save",
		defaultMessage: "Save"
	},
	update: {
		id: "template-form.field.houseOwner.update",
		defaultMessage: "Add"
	},
	delete: {
		id: "template-form.field.houseOwner.delete",
		defaultMessage: "Delete"
	},
	addMore: {
		id: "template-form.field.houseOwner.addMore",
		defaultMessage: "Add more"
	},
	mustBeANumber: {
		id: "template-form.field.houseOwner.mustBeANumber",
		defaultMessage: "Must be a number"
	},
	required: {
		id: "template-form.field.houseOwner.required",
		defaultMessage: "Required"
	},
	ssnAlreadyUsed: {
		id: "template-form.field.houseOwner.ssnAlreadyUsed",
		defaultMessage: "SSN is already in use as a house owner"
	},
	noCustomerFound: {
		id: "template-form.field.houseOwner.noCustomerFound",
		defaultMessage: "No customer found with this SSN"
	},
	canNotUseMainCustomer: {
		id: "template-form.field.houseOwner.canNotUseMainCustomer",
		defaultMessage: "You can not use main customer as extra house owner"
	}
});

const fieldsToTouch = [
	"customerNumber",
	"email",
	"phoneMobile"
] as (keyof ExtendedApplicationCustomer)[];

const fieldsToTouchDictionary = Object.fromEntries(
	fieldsToTouch.map(key => [key, true])
);

function makeHouseOwnerSchema(
	intl: IntlShape,
	validationHelpers: ValidationHelpers
): Yup.ObjectSchema<
	Required<
		Pick<
			ExtendedApplicationCustomer,
			"customerNumber" | "email" | "phoneMobile"
		>
	>,
	Yup.AnyObject,
	Pick<ExtendedApplicationCustomer, "customerNumber" | "email" | "phoneMobile">,
	""
> {
	return Yup.object({
		customerNumber: Yup.string()
			.required(intl.formatMessage(messages.required))
			.test(
				"is-valid-ssn",
				intl.formatMessage(messages.mustBeValidSsn),
				validationHelpers.validSsn
			),
		email: Yup.string().required(intl.formatMessage(messages.required)),
		phoneMobile: Yup.string().required(intl.formatMessage(messages.required))
	});
}

interface SearchParamaters {
	customerNumber: string;
	customerType: CustomerType;
	customerRole: CustomerRole;
	noReplace?: boolean;
}

interface CustomersResponse {
	customers: ApplicationCustomerDetailsDTO[];
}

interface SearchResponse extends CustomersResponse {
	foundCustomer: ApplicationCustomerDetailsDTO;
}

const doApplicationCustomerSearch = (parameters: SearchParamaters) =>
	fetch("/api/application-customers/search", {
		method: "POST",
		body: new Blob([JSON.stringify(parameters)], {
			type: "application/json"
		})
	}).then(r => {
		if (!r.ok) {
			if (r.status === 409)
				return Promise.reject(CustomerMutationErrors.CannotUseMainCustomer);
			return Promise.reject(CustomerMutationErrors.NoCustomerFound);
		}
		return r.json() as Promise<SearchResponse>;
	});

interface RemoveHintParameters {
	customerRole: CustomerRole;
	customerNumberHint?: string;
	customerId?: number;
}
const doApplicationCustomerRemove = (parameters: RemoveHintParameters) =>
	fetch(`/api/application-customers/clear-application-customer`, {
		method: "POST",
		body: new Blob([JSON.stringify(parameters)], {
			type: "application/json"
		})
	}).then(r => {
		if (!r.ok) {
			return null;
		}
		return r.json() as Promise<CustomersResponse>;
	});

const CustomerMutationErrors = {
	NoCustomerFound: "noCustomerFound",
	SsnAlreadyUsed: "ssnAlreadyUsed",
	CannotUseMainCustomer: "cannotUseMainCustomer"
} as const;
type CustomerMutationErrorValues =
	typeof CustomerMutationErrors[keyof typeof CustomerMutationErrors];

function exceptionIsCustomerMutationError(
	e: unknown
): e is CustomerMutationErrorValues {
	return (
		typeof e === "string" &&
		Object.values(CustomerMutationErrors).includes(
			e as CustomerMutationErrorValues
		)
	);
}

// const doApplicationCustomerUpdate = (parameters: LimitedCustomerToUpdate) =>
// 	fetch("/api/application-customers/customer", {
// 		method: "PUT",
// 		body: new Blob([JSON.stringify(parameters)], {
// 			type: "application/json"
// 		})
// 	}).then(res => res.ok);

interface HouseOwnerFromApplicationCustomerProps {
	data: Readonly<ExtendedApplicationCustomer>;
	/**
	 * @returns whether the update succesful or not
	 */
	onUpdate?: (
		data: ExtendedApplicationCustomer
	) => Promise<ExtendedApplicationCustomer>;
	onDelete: (data: ExtendedApplicationCustomer) => Promise<void>;
}

const HouseOwnerFromApplicationCustomerElement = (
	props: HouseOwnerFromApplicationCustomerProps
) => {
	const isMounted = useRef(true);
	useEffect(
		() => () => {
			isMounted.current = false;
		},
		[]
	);

	const classes = useStyles();
	const intl = useIntl();

	const validationHelpers = useMemo(() => createValidationHelpers(), []);

	const ownerSchema = useMemo(
		() => makeHouseOwnerSchema(intl, validationHelpers),
		[intl, validationHelpers]
	);

	const [expanded, setExpanded] = useState(
		!ownerSchema.isValidSync(props.data)
	);

	const { _isNewEntry } = props.data;
	const isExpanded = _isNewEntry || expanded;

	const [isRemoving, setIsRemoving] = useState(false);
	const handleOnRemove = useCallback(async () => {
		setIsRemoving(true);
		// No need to setIsRemoving(false)
		await props.onDelete(props.data);
	}, [props]);

	return (
		<Formik
			initialValues={props.data}
			enableReinitialize
			validationSchema={ownerSchema}
			onSubmit={async (ownerToUpdate, helpers) => {
				if (props.onUpdate == null) {
					return;
				}

				try {
					const result = await props.onUpdate?.(ownerToUpdate);
					isMounted.current && setExpanded(false);
					return result;
				} catch (e) {
					if (exceptionIsCustomerMutationError(e)) {
						if (e === CustomerMutationErrors.NoCustomerFound) {
							// Rejection value
							helpers.setErrors({
								customerNumber: intl.formatMessage(messages.noCustomerFound)
							});
						} else if (e === CustomerMutationErrors.SsnAlreadyUsed) {
							helpers.setErrors({
								customerNumber: intl.formatMessage(messages.ssnAlreadyUsed)
							});
						} else if (e === CustomerMutationErrors.CannotUseMainCustomer) {
							helpers.setErrors({
								customerNumber: intl.formatMessage(
									messages.canNotUseMainCustomer
								)
							});
						}
					}
					return;
				}
			}}
		>
			{formikProps => (
				<Accordion
					expanded={isExpanded}
					onChange={async (_, updatedExpanded) => {
						const errorMessages = await formikProps.validateForm();
						// validateForm() doesn't set formikProps.isValid => false, or formikProps.isDirty => true..
						// Must instead take a look at validationresult to prevent closing of Accordion

						if (Object.keys(errorMessages).length > 0 || !formikProps.isValid) {
							// Prevent closing if there are errors
							formikProps.setTouched(fieldsToTouchDictionary, true);
							return;
						}

						if (!formikProps.dirty) setExpanded(updatedExpanded);
					}}
					className={clsx(classes.listItem, {
						[classes.expanded]: isExpanded
					})}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon className={classes.expandMoreIcon} />}
					>
						{isExpanded ? (
							<Typography className={classes.lenderLabel}>
								{intl.formatMessage(messages.houseOwnerTitle)}
							</Typography>
						) : (
							<Typography className={classes.boldText}>
								{props.data.customerName?.trim() ||
									intl.formatMessage(messages.houseOwnerTitle)}
							</Typography>
						)}
					</AccordionSummary>
					<AccordionDetails>
						<Grid container spacing={4} direction="column">
							<Grid item>
								<FormikField
									name="customerNumber"
									component={TextField}
									label={intl.formatMessage(messages.ssn)}
									errorDisplay={ErrorDisplay.Touched}
									fullWidth
								/>
							</Grid>

							<Grid item>
								<FormikField
									name="email"
									component={TextField}
									label={intl.formatMessage(messages.email)}
									errorDisplay={ErrorDisplay.Touched}
									fullWidth
								/>
							</Grid>
							<Grid item>
								<FormikField
									name="phoneMobile"
									component={TextField}
									label={intl.formatMessage(messages.phone)}
									errorDisplay={ErrorDisplay.Touched}
									fullWidth
								/>
							</Grid>
							{props.onUpdate != null ? (
								<Grid item container justifyContent="space-between">
									<Button
										disabled={
											isRemoving ||
											formikProps.isSubmitting ||
											!formikProps.dirty ||
											!formikProps.isValid
										}
										onClick={() => {
											formikProps.submitForm();
										}}
										variant="contained"
									>
										{_isNewEntry
											? intl.formatMessage(messages.save)
											: intl.formatMessage(messages.update)}
									</Button>
									{props.onDelete != null ? (
										<Button
											onClick={handleOnRemove}
											variant="contained"
											disabled={formikProps.isSubmitting || isRemoving}
										>
											{intl.formatMessage(messages.delete)}
										</Button>
									) : null}
								</Grid>
							) : null}
						</Grid>
					</AccordionDetails>
				</Accordion>
			)}
		</Formik>
	);
};

const PropertyOwnersInternal = memo(
	(props: {
		owners: Readonly<ExtendedApplicationCustomer[]>;
		onAdd?: () => void;
		onUpdate?: (
			owner: ExtendedApplicationCustomer
		) => Promise<ExtendedApplicationCustomer>;
		onDelete: (owner: ExtendedApplicationCustomer) => Promise<void>;
		className?: string;
	}) => {
		const intl = useIntl();
		const classes = useStyles();

		const { owners } = props;

		return (
			<Grid container className={props.className}>
				<Grid container spacing={1}>
					{owners.map(data => (
						<Grid item xs={12} key={data._frontendOnlyId ?? data.customerId}>
							<HouseOwnerFromApplicationCustomerElement
								data={data}
								onUpdate={props.onUpdate}
								onDelete={props.onDelete}
							/>
						</Grid>
					))}
				</Grid>

				{props.onAdd != null ? (
					<Grid item xs={12}>
						<Button
							onClick={props.onAdd}
							variant="outlined"
							className={classes.addButton}
							fullWidth
						>
							<AddCircleOutlineIcon className={classes.addButtonPlusIcon} />
							{intl.formatMessage(messages.addMore)}
						</Button>
					</Grid>
				) : null}
			</Grid>
		);
	}
);

function houseOwnerExistencePredicate(
	itemFromCollection: ExtendedApplicationCustomer,
	needle: ExtendedApplicationCustomer
) {
	return (
		(needle._frontendOnlyId != null &&
			needle._frontendOnlyId === itemFromCollection._frontendOnlyId) ||
		(needle._frontendOnlyId == null &&
			needle.customerId === itemFromCollection.customerId)
	);
}

function ensureCollectionHasIds(items: any): ExtendedApplicationCustomer[] {
	return (
		!isArray(items) ? [] : (items as ExtendedApplicationCustomer[]) || []
	).map(owner =>
		// Just in case application / calc rules are reloaded but owner are not saved to database. May cause rerenders.
		(owner.customerId == null || owner.customerId === 0) &&
		!owner._frontendOnlyId
			? { ...owner, _frontendOnlyId: uuid() }
			: owner
	);
}

export const HouseOwnersFromApplicationCustomersField = memo(
	(props: InputFieldProps) => {
		const classes = useStyles();

		const customers = useMemo(
			() => ensureCollectionHasIds(props.field.value),
			[props.field.value]
		);

		const owners = useMemo(
			() => customers.filter(o => o.customerRole === CustomerRole.HouseOwner),
			[customers]
		);

		const addItem = useCallback(() => {
			const newItem: ExtendedApplicationCustomer = {
				_frontendOnlyId: uuid(),
				_isNewEntry: true,
				customerRole: CustomerRole.HouseOwner,
				customerId: 0
			};
			let updatedList = [...customers, newItem];

			props.form.setFieldValue(props.field.name, updatedList);
		}, [customers, props.field.name, props.form]);

		const deleteItem = useCallback(
			async (ownerToDelete: ExtendedApplicationCustomer) => {
				// TODO: Communicate succesful deletion and errors
				let updatedCustomerList: ApplicationCustomerDetailsDTO[] | undefined =
					[];

				if (ownerToDelete._isNewEntry) {
					updatedCustomerList = props.field.value.filter(
						x => x._frontendOnlyId !== ownerToDelete._frontendOnlyId
					);
				} else {
					updatedCustomerList = await doApplicationCustomerRemove({
						customerRole: CustomerRole.HouseOwner,
						customerNumberHint: ownerToDelete.customerNumber
					}).then(res => res?.customers);
				}

				if (updatedCustomerList != null) {
					props.form.setFieldValue(props.field.name, updatedCustomerList);
					return Promise.resolve();
				}

				// Treat unsuccesful delete as error
				return Promise.reject();
			},
			[props.field.name, props.field.value, props.form]
		);

		const updateItem = useCallback(
			async (updatedData: ExtendedApplicationCustomer) => {
				let relevantCustomerList = customers;
				const oldCustomerObject = customers.find(
					o =>
						o.customerRole === CustomerRole.HouseOwner &&
						updatedData.customerId !== 0 &&
						o.customerId === updatedData.customerId
				);
				const newOrChangedSsn =
					oldCustomerObject == null ||
					oldCustomerObject.customerNumber !== updatedData.customerNumber;
				const ssnIsAlreadyUsed =
					newOrChangedSsn &&
					customers.find(
						o =>
							o.customerRole === CustomerRole.HouseOwner &&
							o.customerNumber === updatedData.customerNumber
					);

				if (ssnIsAlreadyUsed) {
					return Promise.reject(CustomerMutationErrors.SsnAlreadyUsed);
				}

				if (newOrChangedSsn && updatedData.customerNumber != null) {
					if (oldCustomerObject) {
						// Can discard result since we will be looking up new customer and use customer list from that call
						await doApplicationCustomerRemove({
							customerRole: CustomerRole.HouseOwner,
							customerId: oldCustomerObject.customerId
						});
					}

					try {
						const searchResponse = await doApplicationCustomerSearch({
							customerRole: CustomerRole.HouseOwner,
							customerType: CustomerType.Private,
							customerNumber: updatedData.customerNumber
						});
						relevantCustomerList = searchResponse.customers;
						// Make sure we can find our new customer
						updatedData.customerId = searchResponse.foundCustomer.customerId;
						updatedData._frontendOnlyId = undefined;
						updatedData._isNewEntry = false;
					} catch (e) {
						if (exceptionIsCustomerMutationError(e)) {
							return Promise.reject(e);
						}
						return Promise.reject();
					}
				}

				let updatedList = relevantCustomerList.map(customer => {
					// Update all customers with the same customer id
					if (!houseOwnerExistencePredicate(customer, updatedData)) {
						return customer;
					}

					// Safe to assume we will apply our contact data alreay?
					const entry: ExtendedApplicationCustomer = {
						...customer,
						phoneMobile: updatedData.phoneMobile,
						email: updatedData.email
					};

					return entry;
				});

				// TODO: do extra validation? Ensure unique ssns?

				props.form.setFieldValue(props.field.name, updatedList);
				// Find resolve value, slightly omtimistic that the customer will exist in the list
				return updatedList.find(
					x =>
						x.customerRole === CustomerRole.HouseOwner &&
						houseOwnerExistencePredicate(x, updatedData)
				)!;
			},
			[customers, props.field.name, props.form]
		);

		const maxOwners = 5;
		return (
			<Grid
				container
				spacing={1}
				className={clsx(classes.root, props.className)}
				onClick={props.onClick}
			>
				{props.label ? (
					<Grid item xs={12}>
						<FormLabel component="legend">{props.label}</FormLabel>
					</Grid>
				) : null}
				<Grid item xs={12}>
					<PropertyOwnersInternal
						owners={owners}
						onAdd={owners.length < maxOwners ? addItem : undefined}
						onUpdate={updateItem}
						onDelete={deleteItem}
					/>
				</Grid>
			</Grid>
		);
	}
);

export const HouseOwnersFromApplicationCustomersSection = (
	sectionProps: SectionProps
) => {
	// This section started as a custom field 😅
	return (
		<>
			<CalcRuleFormikField
				component={HouseOwnersFromApplicationCustomersField}
				name="Calculator.CUSTOMERS"
				className={sectionProps.className}
				onClick={sectionProps.onClick}
				literal={sectionProps.literal}
			/>
		</>
	);
};

addRegisteredSectionLayout({
	displayName: "HouseOwnersFromApplicationCustomersSection",
	name: "HouseOwnersFromApplicationCustomersSection",
	settings: {}
});

HouseOwnersFromApplicationCustomersField.displayName =
	"HouseOwnersFromApplicationCustomersField";
HouseOwnersFromApplicationCustomersSection.displayName =
	"HouseOwnersFromApplicationCustomersSection";

export const HouseOwnersFromApplicationCustomersFields =
	identityRecordOfFieldEditorOptions({
		HouseOwnersFromApplicationCustomersField: {
			editableOptions: {}
		}
	});
